import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface EbayStoreStatus {
  connected: boolean;
  shop: string;
  listingCounts: { active: number; sold: number; draft: number; errors: number };
  revenue: { soldRevenueCents: number; soldProfitCents: number; activeGmvCents: number };
  topListings: Array<any>;
  recentErrors: Array<any>;
}

interface EtsyStoreStatus {
  connected: boolean;
  active: number;
  error: number;
  pendingPublish: number;
  listings: Array<any>;
}

function dollars(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span className={`inline-block h-2 w-2 rounded-none border border-current ${ok ? 'text-term-green animate-pulse shadow-[0_0_8px_rgba(0,255,65,0.8)] bg-term-green/50' : 'text-red-500 bg-red-500/50'}`} />
  );
}

function StatCard({ label, value, sub, color = 'text-term-green' }: { label: string; value: string | number; sub?: string; color?: string; }) {
  return (
    <div className="term-card p-2 text-left">
      <div className="term-corners opacity-30" />
      <p className="text-[8px] uppercase tracking-widest text-term-green/50">{label}</p>
      <p className={`text-sm font-bold tracking-widest mt-1 ${color}`}>{value}</p>
      {sub && <p className="text-[8px] text-term-green/30 mt-1 uppercase">{sub}</p>}
    </div>
  );
}

export function EbayStorePanel() {
  const [data, setData] = useState<EbayStoreStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await apiFetch('/api/ebay/status');
        if (!res.ok) return;
        const json = await res.json() as EbayStoreStatus;
        if (!cancelled) setData(json);
      } catch (e) {}
    }
    void load();
    const id = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!data) return <p className="text-[10px] text-term-green/40 uppercase tracking-widest animate-pulse">&gt; ESTABLISHING LINK...</p>;

  const { listingCounts: lc, revenue: rev } = data;
  const margin = rev.soldRevenueCents > 0 ? ((rev.soldProfitCents / rev.soldRevenueCents) * 100).toFixed(0) : '0';

  return (
    <div className="space-y-4 font-mono">
      <div className="flex justify-between items-end border-b border-term-border pb-2">
        <div>
          <p className="term-label mb-1">CONNECTION.STATUS</p>
          <div className="flex items-center gap-2">
            <StatusDot ok={data.connected} />
            <span className="text-[11px] uppercase tracking-widest text-term-green/80">LINK_ACTIVE</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <StatCard label="LIVE_NODES" value={lc.active} />
        <StatCard label="DRAFT_NODES" value={lc.draft} color="text-yellow-400" />
        <StatCard label="NET_REVENUE" value={dollars(rev.soldRevenueCents)} sub={`M:${margin}%`} color="text-[#00ffff]" />
        <StatCard label="NET_PROFIT" value={dollars(rev.soldProfitCents)} />
      </div>

      {data.topListings.length > 0 && (
        <div className="term-card p-2">
          <p className="term-label mb-2">ACTIVE_NODES.TOP</p>
          <ul className="space-y-1">
            {data.topListings.slice(0,5).map((l, i) => (
              <li key={i} className="text-[9px] text-term-green/70 flex justify-between border-b border-term-green/10 pb-1">
                <span className="truncate max-w-[150px]">{l.title}</span>
                <span>{dollars(l.price_cents)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function EtsyStorePanel() {
  const [status, setStatus] = useState<EtsyStoreStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [statusRes, listingsRes] = await Promise.all([
          apiFetch('/api/etsy/status'),
          apiFetch('/api/etsy/listings'),
        ]);
        if (!statusRes.ok) return;
        const s = await statusRes.json() as Omit<EtsyStoreStatus, 'listings'>;
        const l = listingsRes.ok ? ((await listingsRes.json()) as { listings: EtsyStoreStatus['listings'] }).listings : [];
        if (!cancelled) setStatus({ ...s, listings: l });
      } catch (e) {}
    }
    void load();
    const id = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!status) return <p className="text-[10px] text-term-green/40 uppercase tracking-widest animate-pulse">&gt; ESTABLISHING LINK...</p>;

  return (
    <div className="space-y-4 font-mono">
      <div className="flex justify-between items-end border-b border-term-border pb-2">
        <div>
          <p className="term-label mb-1">CONNECTION.STATUS</p>
          <div className="flex items-center gap-2">
            <StatusDot ok={status.connected} />
            <span className="text-[11px] uppercase tracking-widest text-term-green/80">LINK_ACTIVE</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <StatCard label="LIVE_NODES" value={status.active} />
        <StatCard label="ERR_NODES" value={status.error} color={status.error > 0 ? "text-red-500" : "text-term-green/40"} />
      </div>

      <div className="term-card p-2">
        <p className="term-label mb-2">ACTIVE_NODES.ALL</p>
        <ul className="space-y-1">
          {status.listings.slice(0,8).map((l, i) => (
            <li key={i} className="text-[9px] flex justify-between border-b border-term-green/10 pb-1">
              <span className={`truncate max-w-[150px] ${l.etsy_status === 'error' ? 'text-red-500' : 'text-term-green/70'}`}>{l.title}</span>
              <span className="opacity-50">[{l.etsy_status}]</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
